<template>
  <b-modal
    id="service-system-update-status-modal"
    cancel-variant="outline-secondary"
    :cancel-title="$t('button.close')"
    centered
    no-close-on-backdrop
    :title="modalTextTitle"
    :loading="loading"
    @ok="handleOk"
    @cancel="handleClose"
  >
    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        class="btn"
        @click="handleClose"
      >
        {{ $t('button.close') }}
      </b-button>
      <n-button-loading
        :loading="loading"
        :disabled="
          (data.status === initialStatus &&
            data.assignedOperatorId === initialOperatorId) ||
            !data.assignedOperatorId
        "
        class="btn"
        variant="primary"
        @submit="handleOk"
      >
        {{ $t('button.confirm') }}
      </n-button-loading>
    </template>
    <validation-observer
      ref="assignOperatorForm"
      #default="{ invalid }"
    >
      <!-- form -->
      <n-form-confirmation
        ref="formConfirmation"
        key="assignOperatorForm"
        :message="$t('general.are_you_sure_you_want_to_confirm_this_operation')"
        :confirm-button-text="`button.confirm`"
        :cancel-button-text="`button.no`"
        :form="$refs.assignOperatorForm"
        :disabled="invalid"
        @submit="submit"
      >
        <div class="service-info mb-2">
          <div class="d-flex">
            <label class="mb-0 font-weight-bold">{{
              $t('field.serviceName')
            }}</label>
            <p class="mb-0">
              {{ serviceName }}
            </p>
          </div>
          <div class="d-flex">
            <label class="mb-0 font-weight-bold">{{
              $t('field.status')
            }}</label>
            <ServiceStatus
              :status="initialStatus"
              style="margin-right: 0 !important"
            />
          </div>
        </div>
        <div class="service-action">
          <!-- <label class="d-none">
            {{ (initialStatus === 'pendingPrice' ||
              initialStatus === 'completed') ? $t('general.action_type') : $t('general.action_type_single') }}
          </label> -->
          <b-form-group>
            <b-form-radio-group
              v-show="false"
              v-model="restartType"
            >
              <b-form-radio
                value="restart"
                class="mb-1"
              >
                {{
                  initialStatus === 'pending' ||
                    initialStatus === 'pendingDispatch'
                    ? $t('field.start_service')
                    : $t('field.restart_service')
                }}
              </b-form-radio>
              <b-form-radio
                v-if="
                  initialStatus === 'pendingPrice' ||
                    initialStatus === 'completed'
                "
                value="reset"
              >
                {{
                  initialStatus === 'pendingPrice'
                    ? $t('field.set_price')
                    : $t('field.reset_price')
                }}
              </b-form-radio>
            </b-form-radio-group>
            <div
              class="p-1"
              style="border-radius: 0.5rem; background: #f8f8f8"
            >
              <small style="color: #898989">{{ statusHint }}</small>
            </div>
          </b-form-group>

          <label class="d-block">
            {{ $t('field.operator') }}
          </label>
          <n-async-single-select
            key="merchantUserId"
            v-model="data.assignedOperatorId"
            repository="operatorForService"
            selection-key="id"
            selection-label="name"
            rules="required"
            :readonly="!canEditOperator"
            :query="{
              isResetService: restartType == 'restart',
              serviceType: data.serviceType,
              serviceCategory: data.serviceCategory,
              projectId: data.projectId
            }"
            always-fresh
            :must-excluded-ids="noExcluded ? [] : activeChannel.helperOperatorIds || []"
            :get-option-label="getLabel"
            :init-options="data.assignedOperatorId"
            :selectable="(option) => !option.isDisabledBreakTime"
            @optionChange="onChangeOperator"
          >
            <template #option="item">
              {{ item.name }}
              <div
                v-if="item.totalServiceCap"
                class="float-right mr-2"
                style="display: flex; align-items: center !important;"
              >
                <div
                  v-if="item.isDisabledBreakTime"
                  class="mr-1"
                  style="margin-bottom: 2px;"
                >
                  <feather-icon
                    v-b-tooltip.hover.left
                    icon="CoffeeIcon"
                    class="text-danger"
                    :title="$t('general.on_break_time')"
                  />
                </div>
                <span>{{ `${item.totalServiceWork}/${item.totalServiceCap}` }}</span>
              </div>
            </template>
          </n-async-single-select>
        </div>
      </n-form-confirmation>
    </validation-observer>

    <hold-duration-modal
      ref="holdDurationRef"
      :loading="loading"
      @cancel="onCloseHoldModal"
      @update="onUpdateHoldModal"
    />

    <cancel-service-modal
      ref="cancelServiceModal"
      :loading="loading"
      :shown="showCancelServiceModal"
      @cancel="onCloseCancelServiceModal"
      @update="onUpdateHoldModal"
    />
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  // BFormCheckbox,
  BFormGroup,
  BFormRadioGroup,
  BFormRadio,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import NFormConfirmation from '@/components/NFormConfirmation.vue'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import useJwt from '@/auth/jwt/useJwt'
import NButtonLoading from '@/components/NButtonLoading.vue'
import ToastificationAvatar from '@core/components/toastification/ToastificationAvatar.vue'
import { validTransitions } from '@/data/serviceSystemStatus'
import FormInput from './assignOperatorFormInput'
import HoldDurationModal from './HoldDurationModal.vue'
import CancelServiceModal from './CancelServiceModal.vue'
import ServiceStatus from './ServiceStatus.vue'

const FileRepository = Repository.get('file')
const ServiceSystemRepository = Repository.get('serviceSystem')

export default {
  components: {
    BModal,
    NFormConfirmation,
    HoldDurationModal,
    BButton,
    NButtonLoading,
    CancelServiceModal,
    NAsyncSingleSelect,
    // BFormCheckbox,
    BFormGroup,
    BFormRadioGroup,
    BFormRadio,
    ServiceStatus,
  },
  props: {
    activeChannel: {
      type: Object,
      required: false,
      default() {
        return {}
      },
    },
    updateCompleteStatusVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    isHelper: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showCancelServiceModal: false,
      loading: false,
      data: {
        serviceSystemId: '',
        status: '',
        remarkMedia: [], // Initialize as an array
        remarkMediaIds: [], // Initialize as an array
        servicePrice: null,
        assignedOperatorId: '',
        departmentId: '',
        serviceType: '',
        serviceCategory: '',
        shortenName: '',
        projectId: '',
      },
      previousStatus: '',
      key: 1,
      initialStatus: null, // Add this to track initial value
      initialOperatorId: '',
      options: [
        { label: 'status.pending', value: 'pending' },
        { label: 'status.processing', value: 'processing' },
        { label: 'status.hold', value: 'hold' },
        { label: 'status.completed', value: 'completed' },
        { label: 'status.cancel', value: 'cancel' },
      ],
      restartType: 'restart',
      modalTitle: 'restart',
    }
  },
  computed: {
    noExcluded() {
      return this.modalTitle === 'resetPrice'
        || this.modalTitle === 'reassignPriceSpecialist'
        || this.modalTitle === 'assignPriceSpecialist'
    },
    modalTextTitle() {
      if (this.modalTitle === 'resetPrice') {
        return this.$t('general.modal_title_reset_price')
      }
      if (this.modalTitle === 'reassignOperator') {
        return this.$t('general.modal_title_reassign_operator')
      }
      if (this.modalTitle === 'reassignPriceSpecialist') {
        return this.$t('general.modal_title_reassign_price_specialist')
      }
      if (this.modalTitle === 'assignOperator') {
        return this.$t('general.modal_title_assign_operator')
      }
      if (this.modalTitle === 'assignPriceSpecialist') {
        return this.$t('general.modal_title_assign_price_specialist')
      }

      return this.$t('general.modal_title_restart_service')
    },
    serviceName() {
      const { name, shortenName } = this.activeChannel || {}
      if (name) {
        return (
            name?.[this.$i18n.locale.split('-')[0]]
            || name?.en
            || name?.zh
            || name?.km
            || name?.vi
            || '-'
        )
      }
      return shortenName
    },
    showStatusHint() {
      return this.initialOperatorId !== this.data.assignedOperatorId
    },

    statusHint() {
      if (this.restartType === 'reset') {
        return this.initialStatus === 'pendingPrice'
          ? this.$t('general.pending_price_hint')
          : this.$t('general.other_pending_price_hint')
      }

      if (this.initialStatus === 'pendingDispatch') {
        return this.$t('general.pending_dispatch_hint')
      }

      return this.initialStatus === 'pending'
        ? this.$t('general.pending_hint')
        : this.$t('general.other_pending_hint')
    },
    userData() {
      return this.$store.state.profile.email
          || this.$store.state.profile.phone
        ? this.$store.state.profile
        : JSON.parse(
          localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName),
        )
    },
    serviceStatus() {
      return this.activeService?.status || 'pending'
    },
    computedOptions() {
      const statusLabels = {
        pendingDispatch: {
          label: 'status.pendingDispatch',
          value: 'pendingDispatch',
        },
        pending: { label: 'status.pending', value: 'pending' },
        accepted: { label: 'status.accepted', value: 'accepted' },
        processing: { label: 'status.processing', value: 'processing' },
        hold: { label: 'status.hold', value: 'hold' },
        pendingPrice: { label: 'status.pendingPrice', value: 'pendingPrice' },
        completed: { label: 'status.completed', value: 'completed' },
        cancel: { label: 'status.cancel', value: 'cancel' },
        resolved: { label: 'status.resolved', value: 'resolved' },
        unresolved: { label: 'status.unresolved', value: 'unresolved' },
        rejected: { label: 'status.rejected', value: 'rejected' },
      }

      if (this.restartType === 'reset') {
        return [statusLabels.pendingPrice]
      }

      if (
        this.initialOperatorId !== this.data.assignedOperatorId
          || this.restartType === 'restart'
      ) {
        return [statusLabels.pending]
      }

      // Get valid transitions for current status
      const allowedTransitions = validTransitions[this.initialStatus] || []

      // Map transitions to option objects
      return [this.initialStatus, ...allowedTransitions].map(
        status => statusLabels[status],
      )
    },
    canEditOperator() {
      return (
        this.$can('manage', 'manage-modify-service')
          && this.activeChannel?.status !== 'resolved'
          && this.activeChannel?.status !== 'cancel'
      )
    },
    canEditStatus() {
      return (
        this.$can('manage', 'manage-modify-service')
          && this.activeChannel?.status !== 'resolved'
          && this.activeChannel?.status !== 'cancelled'
      )
    },
  },
  watch: {
    restartType: {
      handler(value) {
        if (value === 'restart') {
          this.data.status = 'pending'
        } else if (value === 'reset') {
          this.data.status = 'pendingPrice'
        }
      },
      immediate: true,
    },
    updateCompleteStatusVisible(value) {
      if (value) {
        this.$nextTick(() => {
          this.onChangeStatus('completed')
        })
      }
    },
  },
  methods: {
    getLabel(option) {
      return option.name || ''
    },
    onCloseCancelServiceModal() {
      this.showCancelServiceModal = false
      this.$nextTick(() => {
        this.onCloseHoldModal()
      })
    },
    handleClose() {
      this.hide()
      this.data.remark = ''
      this.data.status = ''
      this.data.serviceSystemId = ''
      this.data.remarkMedia = []
      this.data.remarkMediaIds = []
      this.data.servicePrice = null
    },
    show(serviceSystem, type = 'restart', title = 'restart') {
      this.restartType = type
      this.modalTitle = title
      this.key += 1
      this.data = {
        serviceSystemId: serviceSystem.id,
        status: this.restartType === 'reset' ? 'pendingPrice' : 'pending',
        duration: 0,
        remarkMedia: [], // Initialize as an array
        remarkMediaIds: [], // Initialize as an array
        servicePrice: null,
        assignedOperatorId: serviceSystem.assignedOperatorId,
        departmentId: serviceSystem.departmentId,
        serviceType: serviceSystem.serviceType?.code,
        serviceCategory: serviceSystem.serviceCategory?.code,
        shortenName: serviceSystem.shortenName,
        projectId: serviceSystem.projectId,
      }
      this.operatorName = serviceSystem.assignedOperator
      this.initialStatus = serviceSystem?.status // Store initial value
      this.initialOperatorId = serviceSystem.assignedOperatorId
      this.$bvModal.show('service-system-update-status-modal')
    },
    hide() {
      this.data = {}
      this.$bvModal.hide('service-system-update-status-modal')
    },
    onChangeOperator(data) {
      this.operatorName = data?.name
    },
    handleOk(bvModal) {
      if (bvModal) bvModal.preventDefault()
      if (this.loading) return

      if (this.$refs.assignOperatorForm?.validate) {
        this.$refs.assignOperatorForm.validate().then(success => {
          if (success) {
            this.$refs.formConfirmation.confirm()
          }
        })
      } else {
        this.submit()
      }
    },
    async uploadImage(file) {
      const formData = new FormData()
      formData.append('image', file)
      return FileRepository.upload(formData)
    },
    async submit() {
      this.loading = true
      this.$emit('loading', true)
      try {
        let apiRequest = ServiceSystemRepository.updateStatus
        let { status } = this.data || {}

        if (this.isHelper && this.data.status === 'pendingPrice' && !this.modalTitle) {
          apiRequest = ServiceSystemRepository.updateHelperStatus
          status = 'completed'
        }

        const response = await apiRequest(
          {
            status,
            ...(this.data.duration && {
              noOfminuteHold: +this.data.duration,
            }),
            ...(this.data.remark && { remark: this.data.remark.trim() }),
            ...(this.data.remarkMediaIds?.length && {
              remarkMediaIds: this.data.remarkMediaIds,
            }),
            ...(this.data.servicePrice && {
              servicePrice: +this.data.servicePrice,
              servicePriceCurrency: this.activeChannel.servicePriceCurrency,
            }),
            ...((this.data.status !== 'pendingPrice'
                || (this.data.status === 'pendingPrice'
                  && (this.modalTitle === 'resetPrice'
                    || this.modalTitle === 'reassignPriceSpecialist'
                    || this.modalTitle === 'assignPriceSpecialist'))) && {
              assignedOperatorId: this.data.assignedOperatorId,
            }),
            ...(this.data.status === 'pendingPrice' && 
              (this.modalTitle === 'resetPrice' || 
              this.modalTitle === 'reassignPriceSpecialist' || 
              this.modalTitle === 'assignPriceSpecialist'
            ) && {
              allowResetPrice: true,
            }),
          },
          this.data.serviceSystemId,
        )
        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('alert.serviceModifySuccessfully'),
          },
        })
        this.data.remark = ''
        this.data.remarkMedia = []
        this.data.remarkMediaIds = []
        this.data.servicePrice = null
        this.data.assignedOperatorId = ''
        this.data.departmentId = ''
        this.hide()
        this.$emit('hide')
        if (!this.isHelper) {
          this.$emit('success', response)
        }
      } catch (error) {
        if (error.response?.status === 422) {
          this.$refs.assignOperatorForm.setErrors(
              error.response?.data?.validate,
          )
        }
      } finally {
        this.loading = false
        this.showCancelServiceModal = false
        this.$emit('loading', false)
      }
    },
    onChangeStatus(status) {
      if (status === 'hold') {
        this.$refs.holdDurationRef.show(
          this.data.status,
          this.data.serviceSystemId,
        )
      } else if (status === 'cancel') {
        this.showCancelServiceModal = true
      } else if (
        status === 'completed'
          && this.activeChannel.status === 'pendingPrice'
      ) {
        this.hide()
        this.$emit('show-pending-price')
        this.data.status = this.previousStatus
      } else if (status === 'pendingPrice') {
        this.$emit('update-complete-status', this.data)
      } else {
        this.data.duration = 0
      }
    },
    onCloseHoldModal() {
      this.modalTitle = ''
      this.data.status = this.previousStatus
    },
    onUpdateHoldModal(duration, remark) {
      if (duration) this.data.duration = duration
      if (remark) this.data.remark = remark
      this.handleOk()
    },
    onUpdateCompleteStatusModal(
      remark,
      status,
      activeChannel,
      remarkMediaIds,
      servicePrice,
    ) {
      this.modalTitle = ''
      this.data.remark = remark
      if (status) this.data.status = status
      if (activeChannel) this.data.serviceSystemId = activeChannel.id
      if (remarkMediaIds) this.data.remarkMediaIds = remarkMediaIds
      if (servicePrice) this.data.servicePrice = servicePrice
      this.handleOk()
    },
  },
  setup() {
    const formInput = [...FormInput]

    return {
      formInput,
    }
  },
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';

  .service-info {
    background-color: #f8f8f8;
    padding: 1rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;

    label {
      color: #6e6b7b;
      width: 160px;
      font-size: 1rem;
      margin-bottom: 0.75rem !important;
    }

    p {
      color: #5e5873;
      font-size: 1rem;
    }
  }

  .service-action {
    border-radius: 0.5rem;
    padding: 1rem;
    border: 1px solid #eaeaea;
    margin-bottom: 0.5rem;

    label {
      color: #6e6b7b;
      font-size: 1rem;
      font-weight: 500;
    }
  }
</style>
